import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Globe, Copy, Loader2, Check, AlertCircle, Trash2, X, ChevronDown, ChevronRight, ExternalLink,
  LinkIcon, Settings,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  none: { label: 'No domain', color: 'text-stone-400', bg: 'bg-stone-100' },
  pending_verification: { label: 'Pending verification', color: 'text-amber-600', bg: 'bg-amber-50' },
  active: { label: 'Active', color: 'text-green-600', bg: 'bg-green-50' },
  failed: { label: 'Failed', color: 'text-red-600', bg: 'bg-red-50' },
};

const DNS_PROVIDERS = [
  {
    name: 'Cloudflare',
    steps: [
      'Log in to your Cloudflare dashboard',
      'Select your domain, then go to DNS > Records',
      'Click "Add Record"',
      'Type: CNAME, Name: [subdomain], Target: [cname]',
      'Proxy status: DNS only (grey cloud) — important!',
      'Click Save',
    ],
    link: 'https://dash.cloudflare.com',
    note: 'Disable Cloudflare proxy (orange cloud) — set to DNS Only (grey cloud) so SSL works correctly.',
  },
  {
    name: 'GoDaddy',
    steps: [
      'Go to My Products > Domain > DNS',
      'Click "Add" under DNS Records',
      'Type: CNAME, Name: [subdomain], Value: [cname]',
      'TTL: 1 Hour',
      'Click Save',
    ],
    link: 'https://dcc.godaddy.com/manage',
  },
  {
    name: 'Namecheap',
    steps: [
      'Go to Domain List > Manage > Advanced DNS',
      'Click "Add New Record"',
      'Type: CNAME, Host: [subdomain], Value: [cname]',
      'TTL: Automatic',
      'Click the checkmark to save',
    ],
    link: 'https://ap.www.namecheap.com',
  },
  {
    name: 'Route 53 (AWS)',
    steps: [
      'Open Route 53 > Hosted Zones > select your domain',
      'Click "Create Record"',
      'Record name: [subdomain], Record type: CNAME',
      'Value: [cname]',
      'TTL: 300',
      'Click "Create records"',
    ],
    link: 'https://console.aws.amazon.com/route53',
  },
  {
    name: 'Google Domains / Squarespace',
    steps: [
      'Go to DNS settings for your domain',
      'Under Custom records, click "Manage custom records"',
      'Host name: [subdomain], Type: CNAME, Data: [cname]',
      'TTL: 3600',
      'Click Save',
    ],
    link: 'https://domains.google.com',
  },
];

function DNSInstructions({ domain, cnameTarget, onCopy, copied }) {
  const [expandedProvider, setExpandedProvider] = useState(null);

  const parts = domain.split('.');
  const subdomain = parts.length > 2 ? parts.slice(0, -2).join('.') : parts[0];
  const isApex = parts.length <= 2;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-stone-700">DNS Setup Required</p>
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-stone-400 uppercase tracking-wider">Type</p>
            <p className="text-xs font-mono font-semibold text-stone-800">{isApex ? 'A / ALIAS' : 'CNAME'}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-stone-400 uppercase tracking-wider">Name / Host</p>
            <p className="text-xs font-mono text-stone-800">{isApex ? '@' : subdomain}</p>
          </div>
          <button onClick={() => onCopy(isApex ? '@' : subdomain)}
            className="p-1 text-stone-400 hover:text-stone-700 rounded transition-colors" title="Copy">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-stone-400 uppercase tracking-wider">Value / Points to</p>
            <p className="text-xs font-mono text-stone-800 break-all">{cnameTarget}</p>
          </div>
          <button onClick={() => onCopy(cnameTarget)}
            className="p-1 text-stone-400 hover:text-stone-700 rounded transition-colors" title="Copy">
            {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {isApex && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <p className="text-[11px] text-amber-700">
            <strong>Root domain detected.</strong> We recommend using a subdomain (e.g., <code className="bg-amber-100 px-1 rounded">www.{domain}</code> or <code className="bg-amber-100 px-1 rounded">landing.{domain}</code>) as many DNS providers don't support CNAME records on root domains.
          </p>
        </div>
      )}

      <div>
        <p className="text-[11px] font-medium text-stone-500 mb-2">Step-by-step for your provider:</p>
        <div className="space-y-1">
          {DNS_PROVIDERS.map((provider) => (
            <div key={provider.name} className="border border-stone-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedProvider(expandedProvider === provider.name ? null : provider.name)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-stone-50 transition-colors"
              >
                <span className="text-xs font-medium text-stone-700">{provider.name}</span>
                {expandedProvider === provider.name
                  ? <ChevronDown className="w-3 h-3 text-stone-400" />
                  : <ChevronRight className="w-3 h-3 text-stone-400" />
                }
              </button>
              {expandedProvider === provider.name && (
                <div className="px-3 pb-3 border-t border-stone-100">
                  <ol className="list-decimal list-inside space-y-1.5 mt-2">
                    {provider.steps.map((step, i) => (
                      <li key={i} className="text-[11px] text-stone-600">
                        {step
                          .replace('[subdomain]', isApex ? '@' : subdomain)
                          .replace('[cname]', cnameTarget)}
                      </li>
                    ))}
                  </ol>
                  {provider.note && (
                    <p className="text-[10px] text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">{provider.note}</p>
                  )}
                  {provider.link && (
                    <a href={provider.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700 mt-2">
                      Open {provider.name} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
        <p className="text-[11px] text-blue-700">
          DNS changes can take <strong>up to 48 hours</strong> to propagate. Most changes take 5-30 minutes. Click "Verify" after making changes to check if your domain is ready.
        </p>
      </div>
    </div>
  );
}

export default function CustomDomainPanel({ siteId, site, apiBase, getAuthHeader, onSiteUpdate, onClose }) {
  // Per-entity domain endpoint base. Defaults to sites; the Form Flow builder
  // passes `${API}/form-flows/${flowId}` to reuse this panel unchanged.
  const base = apiBase || `${API}/sites/${siteId}`;
  const [domain, setDomain] = useState('');
  const [domainConfig, setDomainConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inputDomain, setInputDomain] = useState('');
  const [orgDomains, setOrgDomains] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);

  const fetchDomain = useCallback(async () => {
    try {
      const res = await axios.get(
        `${base}/domain`,
        { headers: getAuthHeader() }
      );
      setDomainConfig(res.data);
      setDomain(res.data.domain || '');
      setInputDomain(res.data.domain || '');
    } catch {
      // No domain configured
    } finally {
      setLoading(false);
    }
  }, [siteId, getAuthHeader]);

  const fetchOrgDomains = useCallback(async () => {
    setLoadingDomains(true);
    try {
      const res = await axios.get(
        `${API}/sites/available-domains`,
        { headers: getAuthHeader() }
      );
      setOrgDomains(res.data || []);
    } catch {
      // Silently fail — user can still enter manually
    } finally {
      setLoadingDomains(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDomain();
    fetchOrgDomains();
  }, [fetchDomain, fetchOrgDomains]);

  const handleSelectDomain = async (selectedDomain) => {
    setInputDomain(selectedDomain);
    setSaving(true);
    try {
      const res = await axios.put(
        `${base}/domain`,
        { domain: selectedDomain },
        { headers: getAuthHeader() }
      );
      setDomainConfig(res.data);
      setDomain(res.data.domain);
      if (res.data.status === 'active') {
        toast.success('Domain connected and active!');
      } else {
        toast.success('Domain saved — complete DNS setup to activate');
      }
      if (onSiteUpdate) onSiteUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManual = async () => {
    if (!inputDomain.trim()) {
      toast.error('Enter a domain name');
      return;
    }
    await handleSelectDomain(inputDomain.trim());
    setShowManualInput(false);
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this custom domain?')) return;
    setRemoving(true);
    try {
      await axios.delete(
        `${base}/domain`,
        { headers: getAuthHeader() }
      );
      setDomainConfig(null);
      setDomain('');
      setInputDomain('');
      toast.success('Domain removed');
      if (onSiteUpdate) onSiteUpdate();
      fetchOrgDomains();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  const handleVerify = async () => {
    toast.info('Checking DNS records...');
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.post(`${base}/domain/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.verified) {
        toast.success('Domain verified and active!');
      } else {
        toast.info(res.data.message || 'DNS not propagated yet. This can take up to 48 hours.');
      }
    } catch {
      toast.info('DNS not propagated yet. This can take up to 48 hours.');
    }
    await fetchDomain();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const status = domainConfig?.status || 'none';
  const statusConf = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const hasDomain = domain && status !== 'none';

  // Separate verified (available) and unverified org domains
  const verifiedDomains = orgDomains.filter(d => d.verified);
  const availableDomains = verifiedDomains.filter(d => !d.assigned_to || d.assigned_to?.site_id === siteId);
  const assignedElsewhere = verifiedDomains.filter(d => d.assigned_to && d.assigned_to?.site_id !== siteId);

  if (loading) {
    return (
      <div className="w-80 bg-white border border-stone-200 rounded-xl shadow-xl p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Custom Domain
          </h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Current domain display when active */}
        {hasDomain && status === 'active' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Check className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs font-medium text-green-700">Active</span>
            </div>
            <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer"
              className="text-sm font-mono text-green-800 hover:underline flex items-center gap-1">
              {domain} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Current domain display when pending */}
        {hasDomain && status !== 'active' && (
          <div className={`rounded-lg p-3 ${statusConf.bg} border border-stone-200`}>
            <div className="flex items-center gap-2 mb-1">
              {status === 'failed' ? (
                <AlertCircle className="w-3.5 h-3.5 text-red-600" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500" />
              )}
              <span className={`text-xs font-medium ${statusConf.color}`}>{statusConf.label}</span>
            </div>
            <p className="text-sm font-mono text-stone-800">{domain}</p>
          </div>
        )}

        {/* Domain selection — show when no domain or changing */}
        {!hasDomain && (
          <>
            {/* Verified org domains */}
            {loadingDomains ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
              </div>
            ) : verifiedDomains.length > 0 ? (
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-2">
                  Select a verified domain
                </label>
                <div className="space-y-1.5">
                  {availableDomains.map(d => (
                    <button
                      key={d.domain_id}
                      onClick={() => handleSelectDomain(d.domain)}
                      disabled={saving}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-stone-200 rounded-lg hover:border-stone-400 hover:bg-stone-50 transition-all text-left group"
                    >
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-sm text-stone-800 font-mono">{d.domain}</span>
                      </div>
                      <Check className="w-3.5 h-3.5 text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                  {assignedElsewhere.map(d => (
                    <div
                      key={d.domain_id}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm text-stone-500 font-mono">{d.domain}</span>
                      </div>
                      <span className="text-[10px] text-stone-400">Used by {d.assigned_to?.site_name}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-stone-100">
                  {!showManualInput ? (
                    <button
                      onClick={() => setShowManualInput(true)}
                      className="text-xs text-stone-500 hover:text-stone-700 transition-colors"
                    >
                      + Enter a different domain
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={inputDomain}
                        onChange={(e) => setInputDomain(e.target.value)}
                        placeholder="landing.mycompany.com"
                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveManual}
                          disabled={saving || !inputDomain.trim()}
                          className="flex-1 text-xs bg-stone-800 hover:bg-stone-900 text-white"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowManualInput(false); setInputDomain(''); }}
                          className="text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-stone-100">
                  <a
                    href="/settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] text-stone-500 hover:text-stone-700 transition-colors"
                  >
                    <Settings className="w-3 h-3" />
                    Manage domains in Organization Settings
                  </a>
                </div>
              </div>
            ) : (
              /* No org domains — show manual input */
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Domain</label>
                <input
                  type="text"
                  value={inputDomain}
                  onChange={(e) => setInputDomain(e.target.value)}
                  placeholder="landing.mycompany.com"
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                />
                <Button
                  size="sm"
                  onClick={handleSaveManual}
                  disabled={saving || !inputDomain.trim()}
                  className="w-full mt-2 text-xs bg-stone-800 hover:bg-stone-900 text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</>
                  ) : (
                    'Save Domain'
                  )}
                </Button>

                <div className="mt-3 pt-3 border-t border-stone-100">
                  <p className="text-[11px] text-stone-500">
                    Add and verify domains in{' '}
                    <a href="/settings" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Organization Settings
                    </a>{' '}
                    to quickly connect them to any site.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* DNS Instructions for pending domains */}
        {hasDomain && status !== 'active' && (
          <DNSInstructions
            domain={domain}
            cnameTarget={domainConfig?.cname_target || 'sites.nurturecrm.ai'}
            onCopy={copyToClipboard}
            copied={copied}
          />
        )}

        {/* Actions */}
        {hasDomain && (
          <div className="flex gap-2">
            {status !== 'active' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleVerify}
                className="flex-1 text-xs"
              >
                Verify
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRemove}
              disabled={removing}
              className="flex-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
            >
              {removing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <><Trash2 className="w-3 h-3 mr-1" /> Remove</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
